import { SectionTitle } from "@/components/SectionTitle";
import { Box, Container, Typography } from "@mui/material";
import Grid2, { type Grid2Props } from "@mui/material/Unstable_Grid2";
import type { FC, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

const GridItem: FC<PropsWithChildren<Grid2Props>> = ({ children }) => {
  return (
    <Grid2
      xs={12}
      md={6}
      xl={4}
      sx={(theme) => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        boxShadow: `0 0 0 0.5px ${theme.palette.info[300]}`,
        img: {
          height: "100%",
          transformOrigin: "center top",
          transition: "transform 250ms ease-in-out",
        },
        ":hover": {
          "& img": {
            transform: "scale(1.2)",
          },
        },
      })}
    >
      <Box
        px={4}
        py={4}
        sx={(theme) => ({
          height: "100%",
          width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr",
          gridTemplateRows: "400px auto 1fr",
          placeItems: "start center",
          textAlign: "center",

          "& > .MuiTypography-root": {
            mt: theme.spacing(0.25),
            "&.MuiTypography-h4": {
              mt: theme.spacing(3),
            },
          },
        })}
      >
        {children}
      </Box>
    </Grid2>
  );
};

export const AnalysisResultSection = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ py: 8, backgroundColor: "info.100" }}>
      <Container>
        <SectionTitle textAlign="center">{t("why-result.title")}</SectionTitle>
        <Box
          sx={(_theme) => ({
            mt: 7,
            overflow: "hidden",
          })}
        >
          <Grid2
            container
            sx={{
              m: "-1px",
            }}
          >
            <GridItem>
              <Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
                <Box
                  component="img"
                  alt="Result example 1"
                  src="assets/imgs/webp/analysis-results/result1.webp"
                />
              </Box>
              <Typography variant="h4" color="text.darkblue">
                {t("why-result.nutrients.title")}
              </Typography>
              <Typography variant="body2" color="translucent.black.88">
                {t("why-result.nutrients.description")}
              </Typography>
            </GridItem>

            <GridItem>
              <Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
                <Box
                  component="img"
                  alt="Result example 2"
                  src="assets/imgs/webp/analysis-results/result2.webp"
                />
              </Box>
              <Typography variant="h4" color="text.darkblue">
                {t("why-result.food.title")}
              </Typography>
              <Typography variant="body2" color="translucent.black.88" whiteSpace="pre-line">
                {t("why-result.food.description")}
              </Typography>
            </GridItem>

            <GridItem>
              <Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
                <Box
                  component="img"
                  alt="Result example 3"
                  src="assets/imgs/webp/analysis-results/result3.webp"
                />
              </Box>
              <Typography variant="h4" color="text.darkblue">
                {t("why-result.weight.title")}
              </Typography>
              <Typography variant="body2" color="translucent.black.88" whiteSpace="pre-line">
                {t("why-result.weight.description")}
              </Typography>
            </GridItem>

            <GridItem>
              <Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
                <Box
                  component="img"
                  alt="Result example 4"
                  src="assets/imgs/webp/analysis-results/result4.webp"
                />
              </Box>
              <Typography variant="h4" color="text.darkblue">
                {t("why-result.meal.title")}
              </Typography>
              <Typography variant="body2" color="translucent.black.88">
                {t("why-result.meal.description")}
              </Typography>
            </GridItem>

            <GridItem>
              <Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
                <Box
                  component="img"
                  alt="Result example 5"
                  src="assets/imgs/webp/analysis-results/result5.webp"
                />
              </Box>
              <Typography variant="h4" color="text.darkblue">
                {t("why-result.beauty.title")}
              </Typography>
              <Typography variant="body2" color="translucent.black.88" whiteSpace="pre-line">
                {t("why-result.beauty.description")}
              </Typography>
            </GridItem>

            <GridItem>
              <Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
                <Box
                  component="img"
                  alt="Result example 6"
                  src="assets/imgs/webp/analysis-results/result6.webp"
                />
              </Box>
              <Typography variant="h4" color="text.darkblue">
                {t("why-result.concern.title")}
              </Typography>
              <Typography variant="body2" color="translucent.black.88">
                {t("why-result.concern.description")}
                <br />
                <Typography component="span" variant="body3" color="translucent.black.56">
                  *{t("why-result.remark")}
                </Typography>
              </Typography>
            </GridItem>
          </Grid2>
        </Box>
      </Container>
    </Box>
  );
};
